import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import LazyImage from '../components/LazyImage';
import { Calendar, ArrowLeft, ExternalLink, RefreshCw, Tag, Share2 } from 'lucide-react';
import './NewsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to create URL-friendly slug
const createSlug = (title, id) => {
  const slug = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${slug}-${id}`;
};

// Extract ID from slug
const extractIdFromSlug = (slug) => {
  if (!slug) return null;
  const parts = slug.split('-');
  return parts[parts.length - 1];
};

const NewsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [internalNews, setInternalNews] = useState([]);
  const [externalNews, setExternalNews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Todas' },
    { id: 'saude', label: 'Saúde' },
    { id: 'sociedade', label: 'Sociedade' },
    { id: 'psicologia', label: 'Psicologia' },
    { id: 'pais', label: 'País' }
  ];

  useEffect(() => {
    if (slug) {
      loadSingleNews();
    } else {
      loadAllNews();
    }
  }, [slug]);

  const loadAllNews = async () => {
    setLoading(true);
    try {
      const [internalResponse, externalResponse] = await Promise.all([
        axios.get(`${API}/news/public`),
        axios.get(`${API}/rss-news/external?limit=30`)
      ]);
      setInternalNews(internalResponse.data || []);
      setExternalNews(externalResponse.data || []);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSingleNews = async () => {
    setLoading(true);
    try {
      const id = extractIdFromSlug(slug);
      if (id) {
        const response = await axios.get(`${API}/news/public/${id}`);
        setSelectedNews(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar notícia:', error);
      navigate('/noticias');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/rss-news/refresh`);
      setTimeout(async () => {
        const response = await axios.get(`${API}/rss-news/external?limit=30`);
        setExternalNews(response.data || []);
        setRefreshing(false);
      }, 3000);
    } catch (error) {
      console.error('Erro ao atualizar notícias:', error);
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async (news) => {
    const url = `${window.location.origin}/noticias/${createSlug(news.title, news.id)}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: news.title,
          text: news.description,
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copiado!');
    }
  };

  const getFilteredNews = () => {
    let filtered = [...externalNews];
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(n => n.category === selectedCategory);
    }
    return filtered;
  };

  // Loading state
  if (loading) {
    return (
      <div className="news-page">
        <Header />
        <div className="news-hero">
          <div className="news-hero-content">
            <h1>Notícias</h1>
          </div>
        </div>
        <LoadingSpinner fullPage text="A carregar notícias..." />
        <Footer />
      </div>
    );
  }

  // Single news view
  if (slug && selectedNews) {
    const newsUrl = `/noticias/${createSlug(selectedNews.title, selectedNews.id)}`;
    
    return (
      <div className="news-page">
        <SEO 
          title={selectedNews.title}
          description={selectedNews.description || selectedNews.content?.substring(0, 160)}
          image={selectedNews.image_url}
          url={newsUrl}
          type="article"
        />
        <Header />
        <div className="news-hero">
          <div className="news-hero-content">
            <h1>Notícias</h1>
          </div>
        </div>
        <main className="news-content">
          <div className="news-container single-news">
            <Link to="/noticias" className="back-link" data-testid="back-to-news">
              <ArrowLeft size={18} />
              Voltar às notícias
            </Link>
            
            <article className="news-article" data-testid="news-article">
              {selectedNews.image_url && (
                <div className="article-image">
                  <LazyImage 
                    src={selectedNews.image_url} 
                    alt={selectedNews.title}
                    style={{ width: '100%', height: '400px' }}
                  />
                </div>
              )}
              <div className="article-header">
                <div className="article-meta">
                  <Calendar size={16} />
                  {formatDate(selectedNews.created_at)}
                </div>
                <button 
                  className="share-button"
                  onClick={() => handleShare(selectedNews)}
                  data-testid="share-button"
                >
                  <Share2 size={18} />
                  Partilhar
                </button>
              </div>
              <h1>{selectedNews.title}</h1>
              <div className="article-content">
                {selectedNews.content.split('\n').map((paragraph, index) => (
                  paragraph.trim() && <p key={index}>{paragraph}</p>
                ))}
              </div>
            </article>
            
            {/* Related news suggestion */}
            {internalNews.length > 1 && (
              <section className="related-news">
                <h3>Outras Notícias</h3>
                <div className="related-grid">
                  {internalNews
                    .filter(n => n.id !== selectedNews.id)
                    .slice(0, 3)
                    .map(item => (
                      <Link 
                        to={`/noticias/${createSlug(item.title, item.id)}`} 
                        key={item.id} 
                        className="related-card"
                      >
                        <h4>{item.title}</h4>
                        <span className="related-date">
                          <Calendar size={12} />
                          {formatDate(item.created_at)}
                        </span>
                      </Link>
                    ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // News list view
  const filteredExternalNews = getFilteredNews();
  const hasInternalNews = internalNews.length > 0;
  const hasExternalNews = filteredExternalNews.length > 0;

  return (
    <div className="news-page">
      <SEO 
        title="Notícias"
        description="Acompanhe as últimas notícias sobre saúde mental, bem-estar e as atividades da AASPS."
        keywords="notícias, saúde mental, AASPS, atualidades, bem-estar"
        url="/noticias"
      />
      <Header />
      <div className="news-hero">
        <div className="news-hero-content">
          <h1>Notícias</h1>
          <p>Acompanhe aqui as últimas notícias</p>
        </div>
      </div>
      <main className="news-content">
        <div className="news-container">
          
          {/* Internal News Section */}
          {hasInternalNews && (
            <section className="news-section" data-testid="internal-news-section">
              <h2 className="section-title">Notícias da AASPS</h2>
              <div className="news-grid">
                {internalNews.map(item => (
                  <Link 
                    to={`/noticias/${createSlug(item.title, item.id)}`} 
                    key={item.id} 
                    className="news-card internal-news"
                    data-testid={`news-card-${item.id}`}
                  >
                    {item.image_url && (
                      <div className="card-image">
                        <LazyImage 
                          src={item.image_url} 
                          alt={item.title}
                          style={{ width: '100%', height: '200px' }}
                        />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-date">
                        <Calendar size={14} />
                        {formatDate(item.created_at)}
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <span className="read-more">Ler mais →</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* External News Section */}
          <section className="news-section external-section">
            <div className="section-header">
              <h2 className="section-title">Notícias de Saúde Mental</h2>
              <button 
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-news-btn"
              >
                <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
                {refreshing ? 'A atualizar...' : 'Atualizar'}
              </button>
            </div>
            
            {/* Category filters */}
            <div className="category-filters">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            
            {!hasExternalNews ? (
              <LoadingSpinner text="A carregar notícias externas..." />
            ) : (
              <div className="news-grid external-grid">
                {filteredExternalNews.map((item, index) => (
                  <a 
                    href={item.link} 
                    key={`${item.id}-${index}`} 
                    className="news-card external-news"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.image_url && (
                      <div className="card-image">
                        <LazyImage 
                          src={item.image_url} 
                          alt={item.title}
                          style={{ width: '100%', height: '180px' }}
                        />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-source">
                        <span className="source-name">{item.source}</span>
                        <ExternalLink size={12} />
                      </div>
                      <div className="card-date">
                        <Calendar size={14} />
                        {formatDate(item.published_at)}
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.summary}</p>
                      <div className="card-tags">
                        {item.keywords_matched?.slice(0, 3).map((keyword, idx) => (
                          <span key={idx} className="tag">
                            <Tag size={10} />
                            {keyword}
                          </span>
                        ))}
                      </div>
                      <span className="read-more">
                        Ler no site original
                        <ExternalLink size={14} />
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NewsPage;
